import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, getErrorMessage, API_BASE } from "@/lib/api";
import { AGE_STYLES, formatFee, formatBytes } from "@/lib/candidate-utils";
import { NSW_REGIONS } from "@/lib/regions";
import {
  ArrowLeft, Trash, Plus, Note, Paperclip, DownloadSimple, FileText,
  UploadSimple, Bell, CheckCircle, Warning, X, PencilSimple, Sparkle,
  EnvelopeSimple, UsersThree, PaperPlaneTilt, ArrowClockwise, ClockCounterClockwise, Clock, Trophy,
} from "@phosphor-icons/react";

const OUTCOME_OPTIONS = [
  { value: "", label: "—" },
  { value: "waiting", label: "Waiting for feedback" },
  { value: "progressing", label: "Progressing" },
  { value: "second_interview", label: "Second interview" },
  { value: "offer_expected", label: "Offer expected" },
  { value: "offer_made", label: "Offer made" },
  { value: "offered", label: "Offer accepted" },
  { value: "candidate_withdrew", label: "Candidate withdrew" },
  { value: "client_declined", label: "Client declined" },
  { value: "unsuccessful", label: "Unsuccessful" },
  { value: "rejected", label: "Rejected" },
  { value: "rescheduled", label: "Rescheduled" },
  { value: "no_show", label: "No show" },
];
const OUTCOME_STYLES = {
  "": "border-zinc-200 text-zinc-500",
  waiting: "border-amber-500 bg-amber-50 text-amber-700",
  progressing: "border-blue-500 bg-blue-50 text-blue-700",
  second_interview: "border-indigo-500 bg-indigo-50 text-indigo-700",
  offer_expected: "border-purple-500 bg-purple-50 text-purple-700",
  offer_made: "border-emerald-500 bg-emerald-50 text-emerald-700",
  offered: "border-emerald-600 bg-emerald-100 text-emerald-800",
  candidate_withdrew: "border-zinc-500 bg-zinc-100 text-zinc-700",
  client_declined: "border-orange-500 bg-orange-50 text-orange-700",
  unsuccessful: "border-[#FF2A2A] bg-red-50 text-[#B91C1C]",
  rejected: "border-[#FF2A2A] bg-red-50 text-[#B91C1C]",
  rescheduled: "border-amber-500 bg-amber-50 text-amber-700",
  no_show: "border-zinc-500 bg-zinc-100 text-zinc-700",
};

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [team, setTeam] = useState([]);
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [timelineFilter, setTimelineFilter] = useState("all");
  const [error, setError] = useState("");
  const [newNote, setNewNote] = useState("");
  const [editHeader, setEditHeader] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const [cvKey, setCvKey] = useState(0);
  const [summarizing, setSummarizing] = useState(false);
  const [showRecruiterEditor, setShowRecruiterEditor] = useState(false);
  const [placementModal, setPlacementModal] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidates/${id}`);
      setC(data);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  }, [id]);
  const refreshNotes = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidates/${id}/notes`);
      setNotes(data);
    } catch (err) { console.error("Failed to refresh notes:", err); }
  }, [id]);
  const refreshAttachments = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidates/${id}/attachments`);
      setAttachments(data);
    } catch (err) { console.error("Failed to refresh attachments:", err); }
  }, [id]);
  const refreshTimeline = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidates/${id}/timeline`);
      setTimeline(data.events || []);
    } catch (err) { console.error("Failed to refresh timeline:", err); }
  }, [id]);
  const refreshTeam = useCallback(async () => {
    try {
      const { data } = await api.get(`/team`);
      setTeam(data);
    } catch (err) { console.error("Failed to refresh team:", err); }
  }, []);

  useEffect(() => {
    refresh();
    refreshNotes();
    refreshAttachments();
    refreshTeam();
    refreshTimeline();
  }, [refresh, refreshNotes, refreshAttachments, refreshTeam, refreshTimeline]);

  // Re-fetch the timeline whenever the underlying data changes (note added, attachment uploaded, interview updated)
  useEffect(() => {
    refreshTimeline();
  }, [refreshTimeline, notes.length, attachments.length, c?.updated_at]);

  const otherAttachments = useMemo(
    () => attachments.filter((a) => a.id !== c?.cv_attachment_id),
    [attachments, c]
  );

  if (error) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] p-4 rounded-sm">{error}</div>
      </div>
    );
  }
  if (!c) return <div className="text-zinc-500 text-sm uppercase tracking-[0.2em]">Loading</div>;

  const styles = AGE_STYLES[c.age_category];
  const interviews = [...(c.interviews || [])].sort(
    (a, b) => (a.batch - b.batch) || (a.created_at || "").localeCompare(b.created_at || "")
  );
  const bookedCount = interviews.filter((i) => (i.agency_name || "").trim()).length;
  const remaining = Math.max(0, 3 - bookedCount);
  const canUnlockBatch2 = (c.batches || 1) < 2;
  const recruiters = c.recruiters || [];

  const updateField = async (field, value) => {
    try {
      await api.patch(`/candidates/${id}`, { [field]: value });
      refresh();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const saveInterview = async (iid, patch) => {
    try {
      await api.patch(`/candidates/${id}/interviews/${iid}`, patch);
      refresh();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const unlockBatch2 = async () => {
    try {
      await api.post(`/candidates/${id}/interviews/unlock-batch`);
      refresh();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const removeCandidate = async () => {
    if (!window.confirm("Delete this candidate? This cannot be undone.")) return;
    await api.delete(`/candidates/${id}`);
    navigate("/candidates");
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    await api.post(`/candidates/${id}/notes`, { content: newNote });
    setNewNote("");
    refreshNotes();
  };

  const deleteNote = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/candidates/${id}/notes/${nid}`);
    refreshNotes();
  };

  const uploadCV = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/candidates/${id}/cv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCvKey((k) => k + 1);
      refresh();
      refreshAttachments();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const removeCV = async () => {
    if (!window.confirm("Remove the CV?")) return;
    await api.delete(`/candidates/${id}/cv`);
    refresh();
    refreshAttachments();
  };

  const summarizeCV = async () => {
    setSummarizing(true);
    setError("");
    try {
      await api.post(`/candidates/${id}/cv/summarize`);
      refresh();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSummarizing(false);
    }
  };

  const uploadOther = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/candidates/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadKey((k) => k + 1);
      refreshAttachments();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const deleteAttachment = async (aid) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/candidates/${id}/attachments/${aid}`);
    refreshAttachments();
    refresh();
  };

  return (
    <div className="space-y-8" data-testid="candidate-detail-page">
      <BackLink />

      {/* Header */}
      <div className={`bg-white border border-zinc-200 rounded-sm p-6 ${styles.ring}`} data-testid="candidate-header">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className={`inline-block w-2 h-2 rounded-full ${styles.dot}`} />
              <div className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${styles.pill}`}>
                {c.days_on_books} days on books
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Status · {c.status}</div>
              <StatePicker value={c.state || ""} onChange={(v) => updateField("state", v)} />
              {c.state === "NSW" && (
                <RegionPicker value={c.region || ""} onChange={(v) => updateField("region", v)} />
              )}
            </div>
            {editHeader ? (
              <HeaderEditor c={c} onSave={updateField} onDone={() => setEditHeader(false)} />
            ) : (
              <>
                <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl leading-tight" data-testid="candidate-name">
                  {c.name}
                </h1>
                <div className="text-sm text-zinc-500 mt-3">{c.role_target || "No target role set"}</div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <StatusSelect
              current={c.status}
              onChange={(v) => {
                if (v === "placed") {
                  setPlacementModal(true);
                } else {
                  updateField("status", v);
                }
              }}
            />
            {c.status === "placed" && (
              <button
                onClick={() => setPlacementModal(true)}
                data-testid="edit-placement-btn"
                className="border border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-3 py-2.5 rounded-sm text-xs uppercase tracking-[0.15em] font-medium transition-colors"
                title="Change which client placed this candidate"
              >
                Placed with…
              </button>
            )}
            <button onClick={() => setEditHeader((v) => !v)} data-testid="edit-header-btn" className="border border-zinc-200 bg-white text-zinc-950 hover:bg-zinc-100 px-3 py-2.5 rounded-sm transition-colors" title="Edit">
              <PencilSimple size={16} />
            </button>
            <button onClick={removeCandidate} data-testid="delete-candidate-btn" className="border border-zinc-200 bg-white text-zinc-950 hover:bg-zinc-100 px-3 py-2.5 rounded-sm transition-colors" title="Delete candidate">
              <Trash size={16} />
            </button>
          </div>
        </div>

        {/* Header stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-zinc-200 mt-6 pt-6">
          <MetaEditable
            label="Fee approx (A$)"
            value={c.fee_approx}
            editable
            testId="fee-input"
            onSave={(v) => updateField("fee_approx", Number(v || 0))}
            renderValue={(v) => <div className="font-heading font-bold text-2xl">{formatFee(v)}</div>}
          />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Interviews</div>
            <div className="font-heading font-bold text-2xl">
              {bookedCount}<span className="text-zinc-400">/3</span>
            </div>
            {remaining > 0 ? (
              <div className="text-xs text-[#B91C1C] mt-1 flex items-center gap-1">
                <Warning size={11} weight="bold" /> Needs {remaining} more
              </div>
            ) : (
              <div className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                <CheckCircle size={11} weight="bold" /> Fully booked
              </div>
            )}
          </div>
          <div className="md:col-span-1">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Recruiter{recruiters.length > 1 ? "s" : ""}
              </div>
              <button
                onClick={() => setShowRecruiterEditor(true)}
                data-testid="edit-recruiters-btn"
                className="text-zinc-500 hover:text-zinc-950 text-[10px] uppercase tracking-[0.15em]"
              >
                Edit
              </button>
            </div>
            {recruiters.length === 0 ? (
              <div className="text-sm text-zinc-400">Unassigned</div>
            ) : (
              <div className="space-y-1">
                {recruiters.map((r) => (
                  <div key={r.user_id} className="flex items-center justify-between text-sm">
                    <span className="truncate">{r.name}</span>
                    {recruiters.length > 1 && (
                      <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 ml-2">{r.split_pct}%</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Registered</div>
            <div className="text-sm font-medium">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">by {c.created_by_name || "—"}</div>
          </div>
        </div>

        {/* Contact row */}
        <div className="border-t border-zinc-200 mt-4 pt-4 grid md:grid-cols-2 gap-4 max-w-2xl">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
              Candidate email (used for calendar invites)
            </label>
            <input
              type="email"
              data-testid="candidate-email-input"
              defaultValue={c.candidate_email || ""}
              onBlur={(e) => { if (e.target.value !== (c.candidate_email || "")) updateField("candidate_email", e.target.value); }}
              placeholder="jane@example.com"
              className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
              Candidate phone
            </label>
            <input
              type="tel"
              data-testid="candidate-phone-input"
              defaultValue={c.candidate_phone || ""}
              onBlur={(e) => { if (e.target.value !== (c.candidate_phone || "")) updateField("candidate_phone", e.target.value); }}
              placeholder="+61 400 000 000"
              className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
            />
          </div>
        </div>
      </div>

      {/* Reminder banner */}
      {c.status === "active" && remaining > 0 && (
        <div className="bg-zinc-950 text-white p-5 rounded-sm flex items-center gap-4" data-testid="candidate-reminder">
          <Bell size={20} weight="fill" className="text-[#FF2A2A] flex-shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Daily reminder</div>
            <div className="font-heading font-bold text-lg tracking-tight">
              {c.name} needs {remaining} more interview{remaining === 1 ? "" : "s"} to reach 3.
            </div>
          </div>
        </div>
      )}

      {/* CV Quick Access */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="cv-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Quick access</div>
            <div className="font-heading font-bold text-xl tracking-tight flex items-center gap-2">
              <FileText size={18} weight="bold" />
              CV
            </div>
          </div>
          {!c.cv_attachment_id && (
            <label className="bg-zinc-950 text-white px-4 py-2 rounded-sm hover:bg-zinc-800 cursor-pointer text-sm font-medium transition-colors flex items-center gap-2">
              <UploadSimple size={14} weight="bold" /> Attach CV
              <input key={cvKey} type="file" className="hidden" onChange={(e) => uploadCV(e.target.files?.[0])} data-testid="cv-upload-input" />
            </label>
          )}
        </div>

        {c.cv_attachment_id ? (
          <>
            <div className="border border-zinc-200 rounded-sm flex items-center justify-between p-4 flex-wrap gap-3" data-testid="cv-card">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-emerald-50 border border-emerald-500 flex items-center justify-center rounded-sm flex-shrink-0">
                  <FileText size={18} weight="bold" className="text-emerald-700" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{c.cv_filename}</div>
                  {c.cv_uploaded_at && (
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">
                      Uploaded {new Date(c.cv_uploaded_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={summarizeCV}
                  disabled={summarizing}
                  data-testid="cv-summarize-btn"
                  className="border border-zinc-950 bg-white text-zinc-950 hover:bg-zinc-950 hover:text-white px-3 py-2 rounded-sm transition-colors text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Sparkle size={14} weight="fill" />
                  {summarizing ? "Summarising..." : c.cv_summary ? "Re-summarise" : "AI summary"}
                </button>
                <a
                  href={`${API_BASE}/candidates/${id}/cv/download`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cv-view-link"
                  className="border border-zinc-200 bg-white text-zinc-950 hover:bg-zinc-100 px-3 py-2 rounded-sm transition-colors text-sm font-medium flex items-center gap-1.5"
                >
                  <DownloadSimple size={14} weight="bold" /> View
                </a>
                <label className="bg-zinc-950 text-white px-3 py-2 rounded-sm hover:bg-zinc-800 cursor-pointer text-sm font-medium transition-colors">
                  Replace
                  <input key={`replace-${cvKey}`} type="file" className="hidden" onChange={(e) => uploadCV(e.target.files?.[0])} data-testid="cv-replace-input" />
                </label>
                <button onClick={removeCV} data-testid="cv-remove-btn" className="text-zinc-400 hover:text-[#FF2A2A] p-2" title="Remove CV">
                  <Trash size={14} />
                </button>
              </div>
            </div>
            {c.cv_summary && (
              <div className="mt-4 border border-zinc-200 bg-zinc-50 rounded-sm p-4" data-testid="cv-summary-block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 flex items-center gap-1">
                  <Sparkle size={11} weight="fill" /> AI summary
                </div>
                <div className="text-sm whitespace-pre-wrap leading-relaxed">{c.cv_summary}</div>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-sm p-6 text-center">
            No CV yet. Attach one for quick access — you'll be able to open it or auto-summarise it in one click.
          </div>
        )}
      </div>

      {/* Linked deals — same person, other deals */}
      {c.linked_deals && c.linked_deals.length > 0 && (
        <div className="bg-white border border-emerald-200 rounded-sm p-6" data-testid="linked-deals-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-600"></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700">Same candidate · other deals</div>
          </div>
          <div className="text-sm text-zinc-500 mb-4 leading-relaxed">
            You've engaged this person before or after this deal. Their full career with you:
          </div>
          <div className="space-y-2">
            {c.linked_deals.map((ld) => {
              const style = (
                { active: "bg-zinc-100 text-zinc-700",
                  placed: "bg-emerald-100 text-emerald-800",
                  lost: "bg-red-100 text-[#B91C1C]",
                  countered: "bg-amber-100 text-amber-800" }
              )[ld.status] || "bg-zinc-100 text-zinc-700";
              return (
                <Link
                  key={ld.id}
                  to={`/candidates/${ld.id}`}
                  data-testid={`linked-deal-${ld.id}`}
                  className="grid grid-cols-12 gap-3 items-center px-4 py-3 border border-zinc-200 rounded-sm hover:border-zinc-950 hover:bg-zinc-50 transition-colors"
                >
                  <div className="col-span-4 font-medium text-sm truncate">{ld.name}</div>
                  <div className="col-span-4 text-xs text-zinc-500 truncate">
                    {ld.role_target || "—"}
                  </div>
                  <div className="col-span-2 text-xs text-zinc-500 text-right">
                    {ld.placed_at ? new Date(ld.placed_at).toLocaleDateString([], { month: "short", year: "2-digit" }) :
                      ld.created_at ? new Date(ld.created_at).toLocaleDateString([], { month: "short", year: "2-digit" }) : "—"}
                  </div>
                  <div className="col-span-2 text-right">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-sm ${style}`}>
                      {ld.status}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Interviews */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="interviews-section">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Interviews · book 3</div>
            <div className="font-heading font-bold text-xl tracking-tight">Interview slots</div>
          </div>
          <div className="text-xs text-zinc-500">
            Batch {c.batches || 1} of 2 · {bookedCount}/{c.slot_count} slots filled
          </div>
        </div>

        <div className="space-y-3">
          {[1, 2].map((batch) => {
            const batchInterviews = interviews.filter((i) => (i.batch || 1) === batch);
            if (batchInterviews.length === 0) return null;
            return (
              <div key={batch} data-testid={`batch-${batch}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  {batch === 1 ? "First 3 interviews" : "Top-up · next 3 interviews"}
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  {batchInterviews.map((slot, idx) => (
                    <InterviewSlot
                      key={slot.id}
                      slot={slot}
                      index={idx}
                      candidateId={id}
                      candidateEmail={c.candidate_email}
                      onSave={(patch) => saveInterview(slot.id, patch)}
                      onPlace={() => setPlacementModal(true)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {canUnlockBatch2 && (
          <div className="mt-6 border-t border-zinc-200 pt-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-sm font-medium">No offer from the first 3?</div>
                <div className="text-xs text-zinc-500 mt-1">Add another 3 interview slots to keep pushing.</div>
              </div>
              <button
                onClick={unlockBatch2}
                data-testid="unlock-batch2-btn"
                className="bg-zinc-950 text-white px-4 py-2.5 rounded-sm hover:bg-zinc-800 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Plus size={14} weight="bold" /> Add 3 more interviews
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notes + attachments */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="notes-section">
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Activity</div>
            <div className="font-heading font-bold text-xl tracking-tight flex items-center gap-2">
              <Note size={18} weight="bold" /> Notes
            </div>
          </div>

          <form onSubmit={addNote} className="mb-5" data-testid="add-note-form">
            <textarea
              data-testid="new-note-input"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={2}
              placeholder="Drop a note for the team..."
              className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950 resize-none"
            />
            <div className="mt-2 flex justify-end">
              <button type="submit" data-testid="add-note-btn" className="bg-zinc-950 text-white px-4 py-2 rounded-sm hover:bg-zinc-800 transition-colors text-sm font-medium">
                Post note
              </button>
            </div>
          </form>

          {notes.length === 0 ? (
            <div className="text-sm text-zinc-500">No notes yet.</div>
          ) : (
            <ul className="space-y-3">
              {notes.map((n) => {
                const isReply = n.kind === "inbound_reply";
                return (
                  <li
                    key={n.id}
                    data-testid={`note-${n.id}`}
                    className={`pl-4 group ${isReply ? "border-l-2 border-emerald-500 bg-emerald-50/40 py-2 pr-2 rounded-sm" : "border-l-2 border-zinc-950"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1.5">
                        {isReply && <EnvelopeSimple size={11} weight="fill" className="text-emerald-700" />}
                        {n.user_name} · {new Date(n.created_at).toLocaleString()}
                      </div>
                      {!isReply && (
                        <button onClick={() => deleteNote(n.id)} data-testid={`delete-note-${n.id}`} className="text-zinc-300 hover:text-[#FF2A2A] opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap leading-relaxed">{n.content}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="attachments-section">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Extras</div>
              <div className="font-heading font-bold text-xl tracking-tight flex items-center gap-2">
                <Paperclip size={18} weight="bold" /> Other files
              </div>
            </div>
            <label className="bg-zinc-950 text-white px-3 py-2 rounded-sm hover:bg-zinc-800 cursor-pointer text-sm font-medium transition-colors">
              Upload
              <input key={uploadKey} type="file" className="hidden" onChange={(e) => uploadOther(e.target.files?.[0])} data-testid="attachment-upload-input" />
            </label>
          </div>

          {otherAttachments.length === 0 ? (
            <div className="text-sm text-zinc-500">No extras. Attach references, JDs, screenshots, or anything else.</div>
          ) : (
            <ul className="space-y-2">
              {otherAttachments.map((a) => (
                <li key={a.id} data-testid={`attachment-${a.id}`} className="flex items-center justify-between border border-zinc-200 p-3 rounded-sm group hover:border-zinc-300 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{a.filename}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">
                      {formatBytes(a.size)} · {a.uploaded_by_name}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={`${API_BASE}/candidates/${id}/attachments/${a.id}/download`} target="_blank" rel="noreferrer" data-testid={`download-attachment-${a.id}`} className="text-zinc-500 hover:text-zinc-950 p-1.5">
                      <DownloadSimple size={16} weight="bold" />
                    </a>
                    <button onClick={() => deleteAttachment(a.id)} data-testid={`delete-attachment-${a.id}`} className="text-zinc-400 hover:text-[#FF2A2A] p-1.5">
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showRecruiterEditor && (
        <RecruiterEditor
          candidate={c}
          team={team}
          onClose={() => setShowRecruiterEditor(false)}
          onSaved={() => { setShowRecruiterEditor(false); refresh(); }}
        />
      )}

      {placementModal && (
        <PlacementModal
          candidate={c}
          onClose={() => setPlacementModal(false)}
          onSaved={async (placedClientId) => {
            try {
              await api.patch(`/candidates/${id}`, {
                status: "placed",
                placed_client_id: placedClientId,
              });
              setPlacementModal(false);
              refresh();
            } catch (e) {
              setError(getErrorMessage(e));
            }
          }}
        />
      )}

      {/* Timeline (bottom of profile — full deal history) */}
      <TimelineSection
        events={timeline}
        filter={timelineFilter}
        setFilter={setTimelineFilter}
      />
    </div>
  );
}

function ClientEmailPicker({ clientId, value, onChange, onBlur, testId }) {
  const [contacts, setContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!clientId) { setContacts([]); setLoaded(true); return; }
    let cancel = false;
    api.get(`/clients/${clientId}`)
      .then((r) => { if (!cancel) { setContacts(r.data?.contacts || []); setLoaded(true); } })
      .catch(() => { if (!cancel) { setContacts([]); setLoaded(true); } });
    return () => { cancel = true; };
  }, [clientId]);

  // If multiple contacts, render dropdown + optional freeform
  if (loaded && contacts.length >= 2) {
    const currentMatchesContact = contacts.some((c) => (c.email || "").toLowerCase() === (value || "").toLowerCase());
    return (
      <div className="relative">
        <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Client email</label>
        <select
          value={currentMatchesContact ? value : "__custom__"}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__custom__") {
              onChange("");
            } else {
              onChange(v);
              onBlur?.();
            }
          }}
          data-testid={`${testId}-picker`}
          className="w-full bg-white border border-zinc-200 px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-zinc-950"
        >
          <option value="__custom__">— Custom email —</option>
          {contacts.map((c) => (
            <option key={c.id} value={c.email}>
              {c.name}{c.title ? ` (${c.title})` : ""}{c.is_primary ? " · primary" : ""} · {c.email || "no email"}
            </option>
          ))}
        </select>
        {!currentMatchesContact && (
          <input
            type="email"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            data-testid={testId}
            placeholder="Type a custom email"
            className="mt-2 w-full bg-white border border-zinc-200 px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-zinc-950"
          />
        )}
      </div>
    );
  }

  // 0 or 1 contact → plain input (backward compatible)
  return (
    <SlotField
      label="Client email"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      testId={testId}
      placeholder="hiringmgr@company.com"
    />
  );
}

function AgencyAutocomplete({ value, onCommit, testId, disabled }) {
  const [inputVal, setInputVal] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setInputVal(value || ""); }, [value]);

  useEffect(() => {
    const q = inputVal.trim();
    if (!q || !open || q === (value || "").trim()) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clients/search`, { params: { q } });
        if (!cancelled) setSuggestions(data || []);
      } catch (err) {
        if (!cancelled) console.error("Client search failed:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [inputVal, open, value]);

  const commit = (v, prefill) => {
    setInputVal(v);
    setOpen(false);
    if (v !== (value || "")) onCommit(v, prefill);
  };

  return (
    <div className="relative">
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Agency</label>
      <input
        data-testid={testId}
        value={inputVal}
        disabled={disabled}
        onChange={(e) => { setInputVal(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => { setOpen(false); commit(inputVal); }, 150)}
        placeholder="Start typing a company name…"
        autoComplete="off"
        className="w-full bg-white border border-zinc-200 px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-zinc-950"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                commit(s.name, {
                  meeting_with: s.primary_contact_name || "",
                  client_email: s.primary_email || "",
                });
              }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b border-zinc-100 last:border-0"
              data-testid={`${testId}-suggestion-${s.id}`}
            >
              <div className="font-medium text-zinc-950">{s.name}</div>
              {(s.primary_contact_name || s.primary_email) && (
                <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
                  {[s.primary_contact_name, s.primary_email].filter(Boolean).join(" · ")}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      {open && !loading && inputVal.trim() && suggestions.length === 0 && inputVal.trim() !== (value || "").trim() && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg px-3 py-2 text-[11px] text-zinc-500" data-testid={`${testId}-no-match`}>
          Nothing matches — <b className="text-zinc-950">"{inputVal.trim()}"</b> will be added as a new client on save.
        </div>
      )}
    </div>
  );
}

function InterviewSlot({ slot, index, onSave, candidateId, candidateEmail, onPlace }) {
  const [local, setLocal] = useState({
    agency_name: slot.agency_name || "",
    meeting_with: slot.meeting_with || "",
    role: slot.role || "",
    scheduled_at: slot.scheduled_at || "",
    duration_min: slot.duration_min || 60,
    client_email: slot.client_email || "",
    followup_hours: slot.followup_hours || 24,
    outcome: slot.outcome || "",
    invite_sent_client: !!slot.invite_sent_client,
    invite_sent_candidate: !!slot.invite_sent_candidate,
    offer_salary: slot.offer_salary ?? "",
    offer_date: slot.offer_date || "",
    offer_status: slot.offer_status || "",
  });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [showFollowup, setShowFollowup] = useState(false);

  useEffect(() => {
    setLocal({
      agency_name: slot.agency_name || "",
      meeting_with: slot.meeting_with || "",
      role: slot.role || "",
      scheduled_at: slot.scheduled_at || "",
      duration_min: slot.duration_min || 60,
      client_email: slot.client_email || "",
      followup_hours: slot.followup_hours || 24,
      outcome: slot.outcome || "",
      invite_sent_client: !!slot.invite_sent_client,
      invite_sent_candidate: !!slot.invite_sent_candidate,
      offer_salary: slot.offer_salary ?? "",
      offer_date: slot.offer_date || "",
      offer_status: slot.offer_status || "",
    });
  }, [slot.id, slot.agency_name, slot.meeting_with, slot.role, slot.scheduled_at, slot.duration_min, slot.client_email, slot.followup_hours, slot.outcome, slot.invite_sent_client, slot.invite_sent_candidate, slot.offer_salary, slot.offer_date, slot.offer_status]);

  const setAndSave = (patch) => {
    setLocal((p) => ({ ...p, ...patch }));
    onSave(patch);
  };
  const setLocalOnly = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const saveField = (k) => onSave({ [k]: local[k] });

  const isBooked = local.agency_name.trim().length > 0;
  const canSend = isBooked && !!local.scheduled_at && ((local.client_email || "").trim() || (candidateEmail || "").trim());
  const icsUrl = `${API_BASE}/candidates/${candidateId}/interviews/${slot.id}/invite.ics`;
  const wasSent = !!slot.invite_sent_at;
  const scheduleChangedSinceSend = wasSent
    && slot.scheduled_at_updated_at
    && slot.invite_sent_at
    && new Date(slot.scheduled_at_updated_at).getTime() > new Date(slot.invite_sent_at).getTime();
  const history = slot.history || [];
  const followupHours = Number(local.followup_hours || 24);
  const followupDue = (
    local.outcome === "waiting"
    && local.scheduled_at
    && !slot.followup_sent_at
    && new Date().getTime() >= new Date(local.scheduled_at).getTime() + followupHours * 60 * 60 * 1000
  );

  const sendInvites = async (opts) => {
    setSending(true);
    setSendResult(null);
    try {
      const { data } = await api.post(`/candidates/${candidateId}/interviews/${slot.id}/send-invites`, opts);
      setSendResult(data);
      if (data.email_configured && data.sent?.length > 0) {
        // Reflect sent flags in local UI immediately
        setLocal((p) => ({
          ...p,
          invite_sent_client: p.invite_sent_client || data.sent.some((s) => s.kind === "client"),
          invite_sent_candidate: p.invite_sent_candidate || data.sent.some((s) => s.kind === "candidate"),
        }));
      }
    } catch (e) {
      setSendResult({ error: e?.response?.data?.detail || e.message || "Send failed" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid={`interview-slot-${slot.id}`}
      className={`border p-4 rounded-sm ${isBooked ? "border-emerald-500 bg-emerald-50/40" : "border-dashed border-zinc-300 bg-zinc-50/60"}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Slot {index + 1}</div>
        <div className="flex items-center gap-1.5">
          {slot.followup_sent_at && (
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-700 border border-emerald-500 bg-emerald-50 px-1.5 py-0.5">
              <ArrowClockwise size={10} weight="bold" /> Chased
            </div>
          )}
          {followupDue && !slot.followup_sent_at && (
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-amber-800 border border-amber-500 bg-amber-50 px-1.5 py-0.5">
              <Bell size={10} weight="bold" /> Chase due
            </div>
          )}
          {isBooked ? (
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-700">
              <CheckCircle size={11} weight="bold" /> Booked
            </div>
          ) : (
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Empty</div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <AgencyAutocomplete
          value={local.agency_name}
          onCommit={(v, prefill) => {
            const patch = { agency_name: v };
            // Only pre-fill when the slot's own field is empty — never overwrite what the user typed
            if (prefill) {
              if (prefill.meeting_with && !(local.meeting_with || "").trim()) {
                patch.meeting_with = prefill.meeting_with;
              }
              if (prefill.client_email && !(local.client_email || "").trim()) {
                patch.client_email = prefill.client_email;
              }
            }
            setAndSave(patch);
          }}
          testId={`slot-${slot.id}-agency`}
        />
        <SlotField label="Meeting with" value={local.meeting_with} onChange={(v) => setLocalOnly("meeting_with", v)} onBlur={() => saveField("meeting_with")} testId={`slot-${slot.id}-meeting-with`} placeholder="Hiring manager name" />
        <ClientEmailPicker
          clientId={slot.client_id}
          value={local.client_email}
          onChange={(v) => { setLocalOnly("client_email", v); }}
          onBlur={() => saveField("client_email")}
          testId={`slot-${slot.id}-client-email`}
        />
        <SlotField label="Role" value={local.role} onChange={(v) => setLocalOnly("role", v)} onBlur={() => saveField("role")} testId={`slot-${slot.id}-role`} placeholder="Job title" />
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Date & time</label>
            <input
              type="datetime-local"
              data-testid={`slot-${slot.id}-datetime`}
              value={toInputDateTime(local.scheduled_at)}
              onChange={(e) => {
                const iso = e.target.value ? new Date(e.target.value).toISOString() : "";
                setAndSave({ scheduled_at: iso });
              }}
              className="w-full bg-white border border-zinc-200 px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-zinc-950"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Mins</label>
            <input
              type="number"
              min="15"
              step="15"
              data-testid={`slot-${slot.id}-duration`}
              value={local.duration_min}
              onChange={(e) => setLocalOnly("duration_min", Number(e.target.value) || 60)}
              onBlur={() => saveField("duration_min")}
              className="w-full bg-white border border-zinc-200 px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-zinc-950"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Outcome</label>
          <select
            data-testid={`slot-${slot.id}-outcome`}
            value={local.outcome}
            onChange={(e) => setAndSave({ outcome: e.target.value })}
            disabled={!isBooked}
            className={`w-full border px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-zinc-950 ${OUTCOME_STYLES[local.outcome]}`}
          >
            {OUTCOME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Offer tracker — shown once outcome hits an offer stage */}
        {isBooked && ["offer_expected", "offer_made", "offered"].includes(local.outcome) && (
          <div className="border border-emerald-300 bg-emerald-50/70 p-2.5 rounded-sm space-y-2" data-testid={`slot-${slot.id}-offer-panel`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 font-medium flex items-center gap-1">
              <Trophy size={11} weight="bold" /> Offer details
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-emerald-800/80 mb-1">Salary (A$)</label>
                <input
                  type="number"
                  data-testid={`slot-${slot.id}-offer-salary`}
                  value={local.offer_salary}
                  onChange={(e) => setLocalOnly("offer_salary", e.target.value)}
                  onBlur={() => onSave({ offer_salary: local.offer_salary === "" ? null : Number(local.offer_salary) })}
                  placeholder="e.g. 120000"
                  className="w-full bg-white border border-emerald-200 px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-emerald-800/80 mb-1">Offer date</label>
                <input
                  type="date"
                  data-testid={`slot-${slot.id}-offer-date`}
                  value={local.offer_date}
                  onChange={(e) => { setLocalOnly("offer_date", e.target.value); onSave({ offer_date: e.target.value }); }}
                  className="w-full bg-white border border-emerald-200 px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-emerald-800/80 mb-1">Offer status</label>
              <select
                data-testid={`slot-${slot.id}-offer-status`}
                value={local.offer_status}
                onChange={(e) => setAndSave({ offer_status: e.target.value })}
                className="w-full border border-emerald-200 bg-white text-emerald-900 px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-emerald-600"
              >
                <option value="">—</option>
                <option value="pending">Pending — waiting on candidate</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
              </select>
            </div>
            {local.offer_status === "accepted" && (
              <button
                type="button"
                onClick={() => onPlace && onPlace()}
                data-testid={`slot-${slot.id}-convert-placement`}
                className="w-full flex items-center justify-center gap-2 bg-emerald-700 text-white text-xs font-medium px-3 py-2 rounded-sm hover:bg-emerald-800 transition-colors"
              >
                <Trophy size={12} weight="bold" /> Convert to placement
              </button>
            )}
          </div>
        )}

        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Chase timing</label>
          <select
            data-testid={`slot-${slot.id}-followup-hours`}
            value={local.followup_hours}
            onChange={(e) => { const v = Number(e.target.value); setLocalOnly("followup_hours", v); onSave({ followup_hours: v }); }}
            disabled={!isBooked}
            className="w-full border border-zinc-200 bg-white text-zinc-950 px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-zinc-950"
          >
            <option value={12}>12 hours after interview</option>
            <option value={24}>24 hours after interview</option>
            <option value={48}>48 hours after interview</option>
            <option value={72}>3 days after interview</option>
          </select>
        </div>

        <div className="pt-2 border-t border-zinc-200 mt-2 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
            <EnvelopeSimple size={11} weight="bold" /> Calendar invite
          </div>
          <div className="flex items-center gap-3">
            <InviteCheckbox
              label="Client"
              checked={local.invite_sent_client}
              onChange={(v) => setAndSave({ invite_sent_client: v })}
              testId={`slot-${slot.id}-invite-client`}
              disabled={!isBooked}
            />
            <InviteCheckbox
              label="Candidate"
              checked={local.invite_sent_candidate}
              onChange={(v) => setAndSave({ invite_sent_candidate: v })}
              testId={`slot-${slot.id}-invite-candidate`}
              disabled={!isBooked}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => sendInvites({ send_to_client: true, send_to_candidate: true })}
              disabled={!canSend || sending}
              data-testid={`slot-${slot.id}-send-both`}
              className={`text-xs px-2.5 py-1.5 rounded-sm transition-colors disabled:opacity-40 flex items-center gap-1 ${
                scheduleChangedSinceSend
                  ? "bg-amber-500 text-white hover:bg-amber-600"
                  : "bg-zinc-950 text-white hover:bg-zinc-800"
              }`}
            >
              <PaperPlaneTilt size={11} weight="bold" />
              {sending ? "Sending..." : wasSent ? "Resend invites" : "Send invites"}
            </button>
            <a
              href={icsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid={`slot-${slot.id}-ics-download`}
              className={`text-xs px-2.5 py-1.5 border rounded-sm transition-colors ${local.scheduled_at ? "border-zinc-300 text-zinc-700 hover:bg-zinc-100" : "border-zinc-200 text-zinc-300 pointer-events-none"}`}
            >
              .ics
            </a>
            {isBooked && (local.client_email || "").trim() && (
              <button
                onClick={() => setShowFollowup(true)}
                data-testid={`slot-${slot.id}-preview-followup`}
                className="text-xs px-2.5 py-1.5 border border-zinc-300 text-zinc-700 rounded-sm hover:bg-zinc-100 transition-colors flex items-center gap-1"
              >
                <Sparkle size={11} weight="bold" /> Chase preview
              </button>
            )}
          </div>
          {scheduleChangedSinceSend && (
            <div className="text-[11px] text-amber-900 border border-amber-500 bg-amber-50 px-2 py-1.5 rounded-sm flex items-start gap-1.5">
              <Warning size={11} weight="bold" className="mt-0.5 flex-shrink-0" />
              <span>Time changed since the last invite went out — recipients still have the old time. Click <b>Resend invites</b> to send an updated one.</span>
            </div>
          )}
          {sendResult && <SendResultChip result={sendResult} />}
          {history.length > 0 && <RescheduleHistory history={history} />}

          {/* Confirmation Centre — audit trail of what actually went out */}
          <ConfirmationCentre slot={slot} />
        </div>
      </div>
      {showFollowup && (
        <FollowupModal
          candidateId={candidateId}
          slotId={slot.id}
          onClose={() => setShowFollowup(false)}
          onSent={() => { setShowFollowup(false); }}
        />
      )}
    </div>
  );
}

function SendResultChip({ result }) {
  if (result.error) {
    return <div className="text-[11px] text-[#B91C1C] border border-[#FF2A2A] bg-red-50 px-2 py-1 rounded-sm">{typeof result.error === "string" ? result.error : "Send failed"}</div>;
  }
  if (result.email_configured === false) {
    return <div className="text-[11px] text-amber-900 border border-amber-500 bg-amber-50 px-2 py-1 rounded-sm">Email not configured — use the .ics button to download and forward.</div>;
  }
  const sentCount = (result.sent || []).length;
  const failedCount = (result.failed || []).length;
  if (sentCount > 0 && failedCount === 0) {
    return <div className="text-[11px] text-emerald-800 border border-emerald-500 bg-emerald-50 px-2 py-1 rounded-sm">Sent to {sentCount} recipient{sentCount === 1 ? "" : "s"}. ✓</div>;
  }
  if (failedCount > 0) {
    return (
      <div className="text-[11px] text-[#B91C1C] border border-[#FF2A2A] bg-red-50 px-2 py-1 rounded-sm">
        Failed for {failedCount}. {result.failed?.[0]?.error?.slice(0, 90)}
      </div>
    );
  }
  return null;
}

function RescheduleHistory({ history }) {  const [open, setOpen] = useState(false);
  const items = [...history].reverse();
  return (
    <div className="pt-2 border-t border-zinc-200">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="toggle-history"
        className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950 flex items-center gap-1"
      >
        <ClockCounterClockwise size={11} weight="bold" />
        {items.length} reschedule{items.length === 1 ? "" : "s"} {open ? "· hide" : "· show"}
      </button>
      {open && (
        <ul className="mt-2 space-y-1.5">
          {items.map((h, idx) => {
            const from = h.from ? new Date(h.from).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
            const to = h.to ? new Date(h.to).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
            const when = h.changed_at ? new Date(h.changed_at).toLocaleDateString([], { day: "numeric", month: "short" }) : "";
            return (
              <li key={`${h.changed_at || ""}-${h.from || ""}-${h.to || ""}`} className="text-[11px] text-zinc-600 flex items-center gap-1 flex-wrap">
                <span className="text-zinc-400">Moved</span>
                <span className="line-through text-zinc-500">{from}</span>
                <span className="text-zinc-400">→</span>
                <span className="font-medium text-zinc-950">{to}</span>
                <span className="text-zinc-400">on {when}</span>
                {h.changed_by_name && <span className="text-zinc-400">· by {h.changed_by_name}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ConfirmationCentre({ slot }) {
  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };
  const rows = [
    { key: "client_invite", label: "Client invite", sent: !!slot.invite_sent_client, at: slot.invite_sent_at },
    { key: "candidate_invite", label: "Candidate invite", sent: !!slot.invite_sent_candidate, at: slot.invite_sent_at },
    { key: "chase", label: "Feedback chase", sent: !!slot.followup_sent_at, at: slot.followup_sent_at },
  ];
  if (!rows.some((r) => r.sent) && !slot.invite_sent_at) return null;
  return (
    <div className="border-t border-zinc-200 pt-3 mt-1" data-testid={`confirmation-centre-${slot.id}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-medium">Confirmations</div>
      <div className="flex flex-wrap gap-2">
        {rows.map((r) => (
          <div
            key={r.key}
            data-testid={`confirm-${slot.id}-${r.key}`}
            className={`flex items-center gap-1.5 text-[11px] px-2 py-1 border rounded-sm ${
              r.sent
                ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                : "border-zinc-200 bg-zinc-50 text-zinc-500"
            }`}
          >
            {r.sent ? <CheckCircle size={11} weight="fill" /> : <Clock size={11} weight="bold" />}
            <span className="font-medium">{r.label}</span>
            {r.sent && r.at && <span className="text-[10px] opacity-70">· {fmt(r.at)}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}


function InviteCheckbox({ label, checked, onChange, testId, disabled }) {
  return (
    <label className={`flex items-center gap-2 text-xs cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        data-testid={testId}
        className="w-4 h-4 accent-zinc-950"
      />
      {label}
    </label>
  );
}

function SlotField({ label, value, onChange, onBlur, testId, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</label>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        className="w-full bg-white border border-zinc-200 px-2 py-1.5 text-xs rounded-sm focus:outline-none focus:border-zinc-950"
      />
    </div>
  );
}

function StatusSelect({ current, onChange }) {
  const options = [
    { value: "active", label: "Active" },
    { value: "placed", label: "Placed" },
    { value: "countered", label: "Countered — stayed at current role" },
    { value: "lost", label: "Lost — went elsewhere / withdrew" },
  ];
  return (
    <select
      data-testid="status-select"
      value={current}
      onChange={(e) => onChange(e.target.value)}
      className="border border-zinc-200 bg-white text-zinc-950 px-3 py-2.5 rounded-sm text-sm font-medium hover:bg-zinc-50 transition-colors focus:outline-none focus:border-zinc-950"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function PlacementModal({ candidate, onClose, onSaved }) {
  // Build the list of unique clients this candidate has interviewed with
  const interviewClients = useMemo(() => {
    const seen = new Map();
    for (const s of (candidate.interviews || [])) {
      const cid = (s.client_id || "").trim();
      const name = (s.agency_name || "").trim();
      if (!cid && !name) continue;
      const key = cid || name.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, { client_id: cid || null, agency_name: name });
      }
    }
    return Array.from(seen.values());
  }, [candidate]);

  // Also fetch all clients so recruiter can pick one not on the interview list
  const [allClients, setAllClients] = useState([]);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    if (!showAll) return;
    (async () => {
      try {
        const { data } = await api.get("/clients");
        setAllClients(data);
      } catch (e) { /* ignore */ }
    })();
  }, [showAll]);

  const currentPlacedId = candidate.placed_client_id || "";
  // If a slot already has an accepted offer, pre-select that client
  const acceptedOfferClientId = (candidate.interviews || []).find(
    (s) => s.offer_status === "accepted" && s.client_id
  )?.client_id || "";
  const initial =
    currentPlacedId ||
    acceptedOfferClientId ||
    (interviewClients.find((c) => c.client_id)?.client_id || "");

  const [selected, setSelected] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!selected) {
      setErr("Choose which client made the placement.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await onSaved(selected);
    } catch (e) {
      setSaving(false);
      setErr((e && e.message) || "Failed to save");
    }
  };

  const options = showAll
    ? allClients.map((c) => ({ client_id: c.id, agency_name: c.name }))
    : interviewClients.filter((c) => c.client_id); // only ones with a real client_id

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="placement-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Placement</div>
            <div className="font-heading font-bold text-xl tracking-tight">Which client hired {candidate.name.split(" ")[0]}?</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950" data-testid="close-placement-modal">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {options.length === 0 ? (
            <div className="text-sm text-zinc-500">
              No clients found on this candidate's interviews.{" "}
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="underline hover:text-zinc-950"
                data-testid="show-all-clients-btn"
              >
                Pick from all clients
              </button>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto" data-testid="placement-client-list">
              {options.map((opt) => {
                const val = opt.client_id;
                const active = selected === val;
                return (
                  <label
                    key={val}
                    data-testid={`placement-option-${val}`}
                    className={`flex items-center gap-3 px-3 py-2.5 border rounded-sm cursor-pointer transition-colors ${
                      active
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-zinc-200 hover:border-zinc-950"
                    }`}
                  >
                    <input
                      type="radio"
                      name="placement-client"
                      value={val}
                      checked={active}
                      onChange={() => setSelected(val)}
                      className="accent-emerald-600"
                    />
                    <span className="text-sm font-medium text-zinc-950">{opt.agency_name || "(Unnamed client)"}</span>
                  </label>
                );
              })}
            </div>
          )}

          {!showAll && interviewClients.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              data-testid="show-all-clients-btn"
              className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950"
            >
              Not one of these? Pick from all clients
            </button>
          )}

          {err && (
            <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2 rounded-sm">{err}</div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-2.5 rounded-sm font-medium hover:bg-zinc-100 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving || !selected}
              data-testid="confirm-placement-btn"
              className="flex-1 bg-emerald-700 text-white py-2.5 rounded-sm font-medium hover:bg-emerald-800 text-sm disabled:opacity-50"
            >
              {saving ? "Saving…" : "Mark hired"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeaderEditor({ c, onSave, onDone }) {
  const [name, setName] = useState(c.name);
  const [role, setRole] = useState(c.role_target || "");
  return (
    <div className="space-y-2 max-w-md">
      <input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name-input" className="w-full bg-white border border-zinc-200 px-3 py-2 text-2xl font-heading font-bold rounded-sm focus:outline-none focus:border-zinc-950" />
      <input value={role} onChange={(e) => setRole(e.target.value)} data-testid="edit-role-input" placeholder="Target role" className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950" />
      <div className="flex gap-2">
        <button
          onClick={async () => {
            if (name !== c.name) await onSave("name", name);
            if (role !== (c.role_target || "")) await onSave("role_target", role);
            onDone();
          }}
          data-testid="save-header-btn"
          className="bg-zinc-950 text-white px-4 py-2 rounded-sm hover:bg-zinc-800 text-sm font-medium"
        >Save</button>
        <button onClick={onDone} className="border border-zinc-200 bg-white text-zinc-950 hover:bg-zinc-100 px-4 py-2 rounded-sm text-sm font-medium">Cancel</button>
      </div>
    </div>
  );
}

function MetaEditable({ label, value, editable, testId, onSave, renderValue }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || 0);
  useEffect(() => { setDraft(value || 0); }, [value]);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      {editing ? (
        <div className="flex items-center gap-2">
          <input type="number" min="0" step="100" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={testId} className="w-full bg-white border border-zinc-200 px-2 py-1.5 text-sm rounded-sm focus:outline-none focus:border-zinc-950" autoFocus />
          <button onClick={async () => { await onSave(draft); setEditing(false); }} data-testid={`${testId}-save`} className="text-emerald-700 p-1">
            <CheckCircle size={16} weight="bold" />
          </button>
          <button onClick={() => { setDraft(value || 0); setEditing(false); }} className="text-zinc-400 p-1">
            <X size={16} weight="bold" />
          </button>
        </div>
      ) : (
        <button onClick={() => editable && setEditing(true)} data-testid={`${testId}-display`} className={`text-left ${editable ? "hover:opacity-80" : ""}`}>
          {renderValue ? renderValue(value) : <div className="font-heading font-bold text-2xl">{value}</div>}
          {editable && <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mt-0.5">Click to edit</div>}
        </button>
      )}
    </div>
  );
}

function RecruiterEditor({ candidate, team, onClose, onSaved }) {
  const initial = candidate.recruiters || [];
  const [r1, setR1] = useState(initial[0]?.user_id || "");
  const [r2, setR2] = useState(initial[1]?.user_id || "");
  const [split, setSplit] = useState(initial[0]?.split_pct || (initial.length === 2 ? 50 : 100));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const list = [];
      if (r1) list.push({ user_id: r1, split_pct: r2 ? Number(split) : 100 });
      if (r2 && r2 !== r1) list.push({ user_id: r2, split_pct: Number(100 - split) });
      await api.patch(`/candidates/${candidate.id}`, { recruiters: list });
      onSaved();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const both = r1 && r2 && r1 !== r2;

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="recruiter-editor-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Team</div>
            <div className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2">
              <UsersThree size={20} weight="bold" /> Recruiters & split
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950">
            <X size={20} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Recruiter 1</label>
            <select value={r1} onChange={(e) => setR1(e.target.value)} data-testid="editor-r1" className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950">
              <option value="">— None —</option>
              {team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Recruiter 2 (optional)</label>
            <select value={r2} onChange={(e) => setR2(e.target.value)} data-testid="editor-r2" className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950">
              <option value="">— None —</option>
              {team.filter((u) => u.id !== r1).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          {both && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Fee split</label>
                <div className="text-xs font-medium">R1: {split}% · R2: {100 - split}%</div>
              </div>
              <input type="range" min="0" max="100" step="5" value={split} onChange={(e) => setSplit(Number(e.target.value))} data-testid="editor-split" className="w-full accent-zinc-950" />
              <div className="text-[10px] text-zinc-500 mt-1">Fee count toward each recruiter's monthly target proportionally when placed.</div>
            </div>
          )}
          {error && <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2 rounded-sm">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-3 rounded-sm font-medium hover:bg-zinc-100 text-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="editor-save" className="flex-1 bg-zinc-950 text-white py-3 rounded-sm font-medium hover:bg-zinc-800 text-sm disabled:opacity-50">
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/candidates" data-testid="back-link" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 transition-colors">
      <ArrowLeft size={14} weight="bold" /> Back to candidates
    </Link>
  );
}

const STATE_PILL_STYLES = {
  NSW: "border-sky-500 bg-sky-50 text-sky-700",
  QLD: "border-amber-500 bg-amber-50 text-amber-700",
  other: "border-zinc-300 bg-zinc-100 text-zinc-700",
};

function StatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const options = [
    { v: "NSW", label: "NSW" },
    { v: "QLD", label: "QLD" },
    { v: "other", label: "Other" },
    { v: "", label: "Unset" },
  ];
  const pillClass = value ? (STATE_PILL_STYLES[value] || STATE_PILL_STYLES.other) : "border-dashed border-zinc-300 text-zinc-500";

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="candidate-state-pill"
        className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border rounded-sm hover:border-zinc-950 transition-colors ${pillClass}`}
        title="Set candidate state"
      >
        State · {value || "unassigned"}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="candidate-state-picker">
      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mr-1">State</span>
      {options.map((opt) => (
        <button
          key={opt.v || "unset"}
          type="button"
          onClick={() => { onChange(opt.v); setOpen(false); }}
          data-testid={`state-set-${opt.v || "unset"}`}
          className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border rounded-sm transition-colors ${
            (value || "") === opt.v
              ? "border-zinc-950 bg-zinc-950 text-white"
              : "border-zinc-200 text-zinc-600 hover:border-zinc-400"
          }`}
        >
          {opt.label}
        </button>
      ))}
      <button type="button" onClick={() => setOpen(false)} className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950 ml-1">
        <X size={11} weight="bold" />
      </button>
    </div>
  );
}

function RegionPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="candidate-region-pill"
        className={`text-[10px] tracking-[0.05em] px-2 py-1 border rounded-sm hover:border-zinc-950 transition-colors ${
          value
            ? "border-sky-300 bg-sky-50 text-sky-800"
            : "border-dashed border-zinc-300 text-zinc-500"
        }`}
        title="Pick NSW region"
      >
        Region · {value || "unset"}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1" data-testid="candidate-region-picker">
      <select
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(false); }}
        data-testid="region-select-inline"
        autoFocus
        className="bg-white border border-zinc-200 px-2 py-1 text-xs rounded-sm focus:outline-none focus:border-zinc-950"
      >
        <option value="">— Pick a region —</option>
        {NSW_REGIONS.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
      <button type="button" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-950">
        <X size={11} weight="bold" />
      </button>
    </div>
  );
}


const TIMELINE_TYPES = {
  candidate_created: { icon: Sparkle, tint: "text-emerald-600 bg-emerald-50 border-emerald-200", label: "Created" },
  placement: { icon: Trophy, tint: "text-emerald-800 bg-emerald-100 border-emerald-400", label: "Placement" },
  offer: { icon: Trophy, tint: "text-emerald-700 bg-emerald-50 border-emerald-300", label: "Offer" },
  interview_booked: { icon: UsersThree, tint: "text-indigo-700 bg-indigo-50 border-indigo-200", label: "Booked" },
  invite_sent: { icon: EnvelopeSimple, tint: "text-blue-700 bg-blue-50 border-blue-200", label: "Invite" },
  interview_rescheduled: { icon: ArrowClockwise, tint: "text-amber-700 bg-amber-50 border-amber-200", label: "Reschedule" },
  followup_sent: { icon: PaperPlaneTilt, tint: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200", label: "Follow-up" },
  interview_outcome: { icon: CheckCircle, tint: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Outcome" },
  note: { icon: Note, tint: "text-zinc-700 bg-zinc-50 border-zinc-200", label: "Note" },
  attachment: { icon: Paperclip, tint: "text-cyan-700 bg-cyan-50 border-cyan-200", label: "File" },
  client_email: { icon: EnvelopeSimple, tint: "text-purple-700 bg-purple-50 border-purple-200", label: "Client email" },
};

const TIMELINE_FILTERS = [
  { v: "all", label: "All activity" },
  { v: "interviews", label: "Interviews", types: ["interview_booked", "interview_rescheduled", "interview_outcome"] },
  { v: "offers", label: "Offers & placements", types: ["offer", "placement"] },
  { v: "emails", label: "Emails", types: ["invite_sent", "followup_sent", "client_email"] },
  { v: "notes", label: "Notes", types: ["note"] },
  { v: "files", label: "Files", types: ["attachment"] },
];

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const s = Math.round(diffMs / 1000);
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

function TimelineSection({ events, filter, setFilter }) {
  const activeFilter = TIMELINE_FILTERS.find((f) => f.v === filter) || TIMELINE_FILTERS[0];
  const filtered = filter === "all"
    ? events
    : events.filter((e) => (activeFilter.types || []).includes(e.type));

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="timeline-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Deal history</div>
          <div className="font-heading font-bold text-xl tracking-tight flex items-center gap-2">
            <ClockCounterClockwise size={18} weight="bold" />
            Timeline
            <span className="text-xs text-zinc-500 font-normal ml-1">
              · {filtered.length} event{filtered.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {TIMELINE_FILTERS.map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`timeline-filter-${f.v}`}
              className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                filter === f.v
                  ? "bg-zinc-950 text-white border-zinc-950"
                  : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-sm" data-testid="timeline-empty">
          {filter === "all" ? "No activity yet — book an interview or add a note to start the timeline." : "Nothing to show for this filter."}
        </div>
      ) : (
        <div className="relative pl-8">
          {/* vertical rail */}
          <div className="absolute left-3 top-2 bottom-2 w-px bg-zinc-200" aria-hidden />
          <ul className="space-y-4">
            {filtered.map((ev, idx) => {
              const meta = TIMELINE_TYPES[ev.type] || TIMELINE_TYPES.note;
              const Icon = meta.icon;
              return (
                <li key={`${ev.type}-${ev.at}-${idx}`} className="relative" data-testid={`timeline-event-${idx}`}>
                  {/* dot */}
                  <div className={`absolute -left-8 top-0.5 w-6 h-6 rounded-full border flex items-center justify-center ${meta.tint}`}>
                    <Icon size={12} weight="bold" />
                  </div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
                    <span>{meta.label}</span>
                    <span className="text-zinc-300">·</span>
                    <span title={ev.at || ""}>{timeAgo(ev.at)}</span>
                    {ev.actor && (
                      <>
                        <span className="text-zinc-300">·</span>
                        <span className="normal-case tracking-normal text-zinc-500">{ev.actor}</span>
                      </>
                    )}
                  </div>
                  <div className="text-sm font-medium text-zinc-950">{ev.title}</div>
                  {ev.description && (
                    <div className="text-xs text-zinc-600 mt-0.5 whitespace-pre-wrap break-words">
                      {ev.description}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function toInputDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function FollowupModal({ candidateId, slotId, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/candidates/${candidateId}/interviews/${slotId}/followup-preview`)
      .then((r) => {
        setPreview(r.data);
        setSubject(r.data.subject || "");
        setHtml(r.data.html || "");
      })
      .catch((e) => setError(getErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [candidateId, slotId]);

  const send = async () => {
    setSending(true);
    setError("");
    try {
      const { data } = await api.post(
        `/candidates/${candidateId}/interviews/${slotId}/send-followup`,
        { subject, html }
      );
      setResult(data);
      if (data.sent) setTimeout(onSent, 1200);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="followup-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Chase email</div>
            <div className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2">
              <Sparkle size={20} weight="fill" /> Preview & send
            </div>
          </div>
          <button onClick={onClose} data-testid="followup-close" className="text-zinc-500 hover:text-zinc-950">
            <X size={20} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {loading ? (
            <div className="text-sm text-zinc-500 uppercase tracking-[0.2em]">Loading</div>
          ) : (
            <>
              {preview?.already_sent_at && (
                <div className="text-xs text-amber-900 border border-amber-500 bg-amber-50 px-3 py-2 rounded-sm">
                  A chase has already been sent for this slot on {new Date(preview.already_sent_at).toLocaleString()}. Sending again will chase the client a second time.
                </div>
              )}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">To</label>
                <div className="text-sm border border-zinc-200 bg-zinc-50 px-3 py-2 rounded-sm">
                  {preview?.to || <span className="text-zinc-400">No client email on this slot</span>}
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Subject</label>
                <input
                  data-testid="followup-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-zinc-950"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">HTML body — tweak wording</label>
                </div>
                <textarea
                  data-testid="followup-html"
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  rows={10}
                  className="w-full bg-white border border-zinc-200 px-3 py-2 text-xs font-mono rounded-sm focus:outline-none focus:border-zinc-950 resize-y"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Preview</div>
                <div className="border border-zinc-200 rounded-sm p-4 bg-white" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
              </div>
              {result && result.email_configured === false && (
                <div className="border border-amber-500 bg-amber-50 text-amber-900 text-xs px-3 py-2 rounded-sm">
                  Resend not configured yet. Wording saved for next time.
                </div>
              )}
              {result && result.sent && (
                <div className="border border-emerald-500 bg-emerald-50 text-emerald-900 text-xs px-3 py-2 rounded-sm">
                  Sent to {result.to} ✓
                </div>
              )}
              {error && (
                <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2 rounded-sm">{error}</div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={onClose} className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-3 rounded-sm font-medium hover:bg-zinc-100 text-sm">Cancel</button>
                <button
                  onClick={send}
                  disabled={sending || !preview?.to}
                  data-testid="followup-send-btn"
                  className="flex-1 bg-zinc-950 text-white py-3 rounded-sm font-medium hover:bg-zinc-800 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <PaperPlaneTilt size={14} weight="bold" />
                  {sending ? "Sending..." : "Send chase now"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
